/* -----------------------------------------------------------------------------------------------------
  - Created: [31-01-2025] by [SREERAJ]
  - Modified: [YYYY-MM-DD] 
  - Description: This file contains Classifications list and modals
------------------------------------------------------------------------------------------------------- */
import React, { useEffect, useState } from 'react';
import {
  get_classification_by_id,
  get_classifications_with_pagination,
  classification_creation,
  classification_deletion,
  classification_update,
} from '../../../../config/SettingsApis/classificationApis';
import CustomBottomPagination from '../../../../Components/custom_components/CustomBottomPagination/CustomBottomPagination';
import CustomTable from '../../../../Components/custom_components/CustomTable/CustomTable';
import CustomModal from '../../../../Components/custom_components/CustomModal/CustomModal';
import { ErrorToast, SuccessToast } from '../../../../utils/toast';
import CustomDeleteConfirmModal from '../../../../Components/custom_components/CustomDeleteConfirmModal/CustomDeleteConfirmModal';
import { handlePaginationEntries } from '../../../../utils/pagination';
import { useSelector } from 'react-redux';
import { hasPrivilege } from '../../../../utils/privileges';
import Loader from '../../../../Components/Loader/Loader';
import { Icons } from '../../../../utils/GlobalIcons';
import CustomTooltipWithoutRoundBorder from '../../../../Components/CustomActionButtonWithTooltip/CustomTooltipWithoutRoundBorder';
import { getStatusColors } from '../../../../utils/constants';
import ClassificationSearchFilter from '../ClassificationSearchFilter/ClassificationSearchFilter';
import ClassificationForm from '../ClassificationForm/ClassificationForm';

function ClassificationTable() {
  const privileges = useSelector((state) => state.privileges.privileges);

  const [classificationListing, setClassificationListing] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalItemPerPage, setTotalItemPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(0);
  const [refresh, setRefresh] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState({ isOpen: false, id: '' });
  const [modalProps, setModalProps] = useState({
    isOpen: false,
    id: '',
    formValues: {},
  });

  // pagination info
  const [pagination, setPagination] = useState({ start: 0, end: 0, totalEntries: 0 });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    getClassifications();
  }, [pageNumber, totalItemPerPage, refresh]);

  // fetch classification list
  const getClassifications = async () => {
    const formData = new FormData();
    formData.append('page', pageNumber);
    formData.append('pageItemcount', totalItemPerPage);

    try {
      setIsLoading(true);
      const response = await get_classifications_with_pagination(formData);

      if (response?.status === 'success') {
        setClassificationListing(response?.data);
        // setTotalPages(response.data.last_page);
        // setPagination(handlePaginationEntries(response.data.from, response.data.to, response.data.total));
      } else {
        setClassificationListing([]);
      }
    } catch (error) {
      setClassificationListing([]);
    } finally {
      setIsLoading(false);
    }
  };

  // permissions
  const PERMISSION_ACTIONS = { view: 4, edit: 5, history: 6, add: 3 };

  const userPermissions = {
    view: hasPrivilege(privileges, 'Settings', 10, PERMISSION_ACTIONS.view),
    edit: hasPrivilege(privileges, 'Settings', 10, PERMISSION_ACTIONS.edit),
    history: hasPrivilege(privileges, 'Settings', 10, PERMISSION_ACTIONS.history),
    add: hasPrivilege(privileges, 'Settings', 10, PERMISSION_ACTIONS.add),
  };

  const hasActionPermissions = userPermissions.view || userPermissions.edit || userPermissions.history;

  // columns
  const columns = [
    { title: '#', key: 'key' },
    { title: 'Classification', key: 'classification' },
    { title: 'Description', key: 'classificationDescription' },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        const statusText = record?.statusId == 1 ? `Active` : 'Inactive' || 'Unknown';
        const statusCode = record?.statusId || 'Unknown'
       
        const style = getStatusColors(statusCode);
        return (
          <span className="status-badge" style={style}>
            {statusText}
          </span>
        );
      },
    },
    { title: 'Actions', key: 'actions', render: (text, row) => renderActions(row) },
  ].filter((column) => column.key !== 'actions' || hasActionPermissions);

  // action buttons
  const renderActions = (row) => (
    <div className="classification-action-container">
      {userPermissions.edit && (
        <CustomTooltipWithoutRoundBorder title="Edit">
          <Icons.Edit className="edit-action-icon" onClick={() => handleModal(true, row?.id)} />
        </CustomTooltipWithoutRoundBorder>
      )}
    </div>
  );

  // prepare table rows
  const datas = () =>
    classificationListing?.map((item, index) => ({
      key: pageNumber * totalItemPerPage - (totalItemPerPage - (index + 1)),
      id: item.id,
      classification: item.ClassificationName,
      // status: item?.Status,
      statusId: item.Status,
      classificationDescription: item.Description,
    }));

  // add/edit modal
  const handleModal = async (isOpen, classificationId) => {
    if (classificationId) {
      getClassificationById({ classificationId });
    } else {
      setModalProps({
        isOpen,
        id: '',
        formValues: {
          ClassificationName: '',
          Status: 1,
          Description: '',
        },
      });
    }
  };

  // fetch single classification
  const getClassificationById = async (params) => {
    try {
      const response = await get_classification_by_id(params);
      if (response?.status === 'success') {
        setModalProps({
          isOpen: true,
          id: response?.data?.id,
          formValues: {
            ClassificationName: response?.data?.ClassificationName,
            Status: response?.data?.Status,
            Description: response?.data?.Description || '',
          },
        });
      } else {
        ErrorToast(response?.message || 'Something Went Wrong!');
      }
    } catch (error) {
      ErrorToast('Something Went Wrong!');
    }
  };

  // delete modal
  const handleDeleteModal = (isOpen, id = '') => setIsDeleteModalOpen({ isOpen, id });

  // save (add/edit)
  const handleClassificationSave = async (values, classificationID) => {
    
    const formData = new FormData();
    for (const key in values) {
      formData.append(key, values[key]);
    }

    try {
      setIsButtonDisabled(true);

      if (classificationID) {
        formData.append('classificationID', classificationID);
      }

      const apiFunction = classificationID ? classification_update : classification_creation;
      const response = await apiFunction(formData);

      const message = response?.data?.message || 'Something went wrong!';
      const status = response?.data?.status;

      if (status === 'success') {
        SuccessToast(message);
        handleModal(false);
        setRefresh(!refresh);
      } else {
        ErrorToast(message);
      }
    } catch (error) {
      ErrorToast('Something went wrong!');
    } finally {
      setIsButtonDisabled(false);
    }
  };

  // delete classification
  const handleDeleteClassification = async () => {
    const params = { classificationId: isDeleteModalOpen?.id };
    try {
      const response = await classification_deletion(params);
      if (response?.data?.status === 'success') {
        SuccessToast(response?.data?.message);
        handleDeleteModal(false, '');
        setRefresh(!refresh);
      } else {
        ErrorToast(response?.data?.message || 'Something Went Wrong!');
      }
    } catch (error) {
      ErrorToast('Something Went Wrong!');
    }
  };

  return (
    <div className="classification-table-container">
      <ClassificationSearchFilter
        tableTitle="Classifications"
        isAddEdiModalOpen={modalProps.isOpen}
        handleAddEditModal={handleModal}
        isAddPermission={userPermissions?.add}
      />

      <CustomTable columns={columns} rows={isLoading ? [] : datas()} isLoading={isLoading} />

      {/* Pagination */}
      {isLoading ? (
        <Loader />
      ) : (
        <CustomBottomPagination
          items_per_page={totalItemPerPage}
          current_page={pageNumber}
          total_page_count={totalPages}
          on_change_value={setPageNumber}
          on_page_count_update={setTotalItemPerPage}
          pagination={pagination}
        />
      )}

      {/* Add/Edit Modal */}
      {modalProps.isOpen && (
        <CustomModal
          open={modalProps.isOpen}
          handleClose={() => handleModal(false)}
          title={modalProps.id !== '' ? 'Edit Classification' : 'Create Classification'}
          body={
            <ClassificationForm
              isButtonDisabled={isButtonDisabled}
              handleClassificationSave={handleClassificationSave}
              id={modalProps.id}
              formIntialValues={modalProps.formValues}
            />
          }
          maxWidth="1000px"
        />
      )}

      {/* Delete Modal */}
      {isDeleteModalOpen?.isOpen && (
        <CustomDeleteConfirmModal
          module_name={'Classification'}
          open_modal={isDeleteModalOpen?.isOpen}
          close_modal={handleDeleteModal}
          confirm_function={handleDeleteClassification}
        />
      )}

    </div>
  );
}

export default ClassificationTable;
